import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Eye, EyeOff } from "lucide-react";
import { getStaff, createStaff, updateStaff, resetStaffPassword, deleteStaff, getPermissions, updateRolePermissions, resetPermissions } from "../lib/api";
import { STAFF_PALETTE, colorForStaff } from "../lib/staffColors";
import { useAuth } from "../App";

// Inline swatch picker for assigning a staff member their calendar color.
const ColorSwatchPicker = ({ value, onChange }) => (
  <div className="flex flex-wrap gap-2" data-testid="staff-color-picker">
    {STAFF_PALETTE.map((c) => (
      <button
        key={c}
        type="button"
        onClick={() => onChange(c)}
        data-testid={`staff-color-${c}`}
        aria-label={`Color ${c}`}
        className={`w-7 h-7 rounded-full border-2 transition-transform ${value === c ? "border-slate-800 scale-110" : "border-transparent hover:scale-105"}`}
        style={{ backgroundColor: c }}
      />
    ))}
  </div>
);

export default function PermissionsPage() {
  const { isAdmin } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [staffDialogOpen, setStaffDialogOpen] = useState(false);
  const [resetPasswordStaffId, setResetPasswordStaffId] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [showResetPw, setShowResetPw] = useState(false);
  const [showNewStaffPw, setShowNewStaffPw] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  
  // Permissions state
  const [rolePermissions, setRolePermissions] = useState({});
  const [availablePermissions, setAvailablePermissions] = useState({});
  const [editingRole, setEditingRole] = useState(null);
  const [editPermissions, setEditPermissions] = useState([]);
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [savingPermissions, setSavingPermissions] = useState(false);
  
  const [newStaff, setNewStaff] = useState({
    name: "",
    email: "",
    password: "",
    dob: "",
    positions: [],
    emergency_contact_name: "",
    emergency_contact_phone: "",
    address: "",
    personal_phone: "",
    work_phone: "",
  });

  const [editStaffDialogOpen, setEditStaffDialogOpen] = useState(false);
  const [editStaffData, setEditStaffData] = useState({});

  const fetchData = async () => {
    try {
      const [staffRes, permissionsRes] = await Promise.all([
        getStaff(),
        getPermissions(),
      ]);
      setStaff(staffRes.data.sort((a, b) => a.name.localeCompare(b.name)));
      setRolePermissions(permissionsRes.data.roles || {});
      setAvailablePermissions(permissionsRes.data.available_permissions || {});
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEditPermissions = (role) => {
    setEditingRole(role);
    setEditPermissions([...(rolePermissions[role] || [])]);
    setPermissionDialogOpen(true);
  };

  const handleTogglePermission = (permission) => {
    setEditPermissions(prev => 
      prev.includes(permission) 
        ? prev.filter(p => p !== permission)
        : [...prev, permission]
    );
  };

  const handleSavePermissions = async () => {
    setSavingPermissions(true);
    try {
      await updateRolePermissions(editingRole, editPermissions);
      toast.success(`Permissions updated for ${editingRole}`);
      setPermissionDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error("Failed to update permissions");
    } finally {
      setSavingPermissions(false);
    }
  };

  const handleResetPermissions = async () => {
    if (!window.confirm("Are you sure you want to reset all permissions to defaults?")) return;
    try {
      await resetPermissions();
      toast.success("Permissions reset to defaults");
      fetchData();
    } catch (err) {
      toast.error("Failed to reset permissions");
    }
  };

  const handleCreateStaff = async (e) => {
    e.preventDefault();
    if (!newStaff.name || !newStaff.email || !newStaff.password) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      await createStaff(newStaff);
      toast.success("Staff member created");
      setStaffDialogOpen(false);
      setNewStaff({
        name: "",
        email: "",
        password: "",
        dob: "",
        positions: [],
        emergency_contact_name: "",
        emergency_contact_phone: "",
        address: "",
        personal_phone: "",
        work_phone: "",
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create staff");
    }
  };

  const handleDeleteStaff = async (staffId) => {
    if (!window.confirm("Are you sure you want to delete this staff member?")) return;
    try {
      await deleteStaff(staffId);
      toast.success("Staff member deleted");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete staff");
    }
  };

  const handleOpenEditStaff = (member) => {
    setEditStaffData({
      id: member.id,
      name: member.name || "",
      email: member.email || "",
      dob: member.dob || "",
      positions: member.positions || [],
      emergency_contact_name: member.emergency_contact_name || "",
      emergency_contact_phone: member.emergency_contact_phone || "",
      address: member.address || "",
      city: member.city || "",
      state: member.state || "",
      zip_code: member.zip_code || "",
      personal_phone: member.personal_phone || "",
      work_phone: member.work_phone || "",
      hire_date: member.hire_date || "",
      color: member.color || colorForStaff(member),
    });
    setEditStaffDialogOpen(true);
  };

  const handleSaveEditStaff = async (e) => {
    e.preventDefault();
    if (!editStaffData.name) {
      toast.error("Name is required");
      return;
    }
    try {
      const { id, ...updates } = editStaffData;
      await updateStaff(id, updates);
      toast.success("Staff member updated");
      setEditStaffDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update staff");
    }
  };

  const toggleEditPosition = (position) => {
    setEditStaffData((prev) => ({
      ...prev,
      positions: prev.positions.includes(position)
        ? prev.positions.filter((p) => p !== position)
        : [...prev.positions, position],
    }));
  };

  // School district management has moved to Contacts ▸ Sending School Set Up.

  const togglePosition = (position) => {
    setNewStaff((prev) => ({
      ...prev,
      positions: prev.positions.includes(position)
        ? prev.positions.filter((p) => p !== position)
        : [...prev.positions, position],
    }));
  };

  const getPositionBadgeColor = (position) => {
    const colors = {
      Admin: "bg-red-100 text-red-800",
      Teacher: "bg-blue-100 text-blue-800",
      Clinician: "bg-purple-100 text-purple-800",
      "Special Educator": "bg-green-100 text-green-800",
      "HQ Viewer": "bg-sky-100 text-sky-800",
    };
    return colors[position] || "bg-gray-100 text-gray-800";
  };

  if (!isAdmin()) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">You don&apos;t have permission to access this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="permissions-page">
      <div>
        <h1 className="text-3xl font-bold text-[#00305E] tracking-tight">Permissions & Settings</h1>
        <p className="text-muted-foreground mt-1">Manage staff and permissions</p>
      </div>

      <Tabs defaultValue="staff" className="space-y-6">
        <TabsList>
          <TabsTrigger value="staff" data-testid="tab-staff">Staff Management</TabsTrigger>
        </TabsList>

        {/* STAFF TAB */}
        <TabsContent value="staff" className="space-y-6">
          <div className="flex justify-end">
            <Dialog open={staffDialogOpen} onOpenChange={setStaffDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="add-staff-btn">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>
                  </svg>
                  Add Staff Member
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add New Staff Member</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateStaff} className="space-y-4 pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Full Name *</Label>
                      <Input
                        value={newStaff.name}
                        onChange={(e) => setNewStaff((p) => ({ ...p, name: e.target.value }))}
                        required
                        data-testid="staff-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={newStaff.email}
                        onChange={(e) => setNewStaff((p) => ({ ...p, email: e.target.value }))}
                        required
                        data-testid="staff-email-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Password *</Label>
                      <div className="relative">
                        <Input
                          type={showNewStaffPw ? "text" : "password"}
                          value={newStaff.password}
                          onChange={(e) => setNewStaff((p) => ({ ...p, password: e.target.value }))}
                          required
                          className="pr-10"
                          data-testid="staff-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewStaffPw((v) => !v)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          aria-label={showNewStaffPw ? "Hide password" : "Show password"}
                          data-testid="toggle-staff-password-visibility"
                        >
                          {showNewStaffPw ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Date of Birth</Label>
                      <Input
                        type="date"
                        value={newStaff.dob}
                        onChange={(e) => setNewStaff((p) => ({ ...p, dob: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Positions</Label>
                    <div className="flex flex-wrap gap-4">
                      {["Admin", "Teacher", "Clinician", "Special Educator", "HQ Viewer"].map((pos) => (
                        <div key={pos} className="flex items-center space-x-2">
                          <Checkbox
                            id={`pos-${pos}`}
                            checked={newStaff.positions.includes(pos)}
                            onCheckedChange={() => togglePosition(pos)}
                          />
                          <Label htmlFor={`pos-${pos}`} className="cursor-pointer">{pos}</Label>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Calendar Color</Label>
                    <ColorSwatchPicker value={newStaff.color} onChange={(c) => setNewStaff((p) => ({ ...p, color: c }))} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Emergency Contact Name</Label>
                      <Input
                        value={newStaff.emergency_contact_name}
                        onChange={(e) => setNewStaff((p) => ({ ...p, emergency_contact_name: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Emergency Contact Phone</Label>
                      <Input
                        value={newStaff.emergency_contact_phone}
                        onChange={(e) => setNewStaff((p) => ({ ...p, emergency_contact_phone: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Address</Label>
                    <Input
                      value={newStaff.address}
                      onChange={(e) => setNewStaff((p) => ({ ...p, address: e.target.value }))}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Personal Phone</Label>
                      <Input
                        value={newStaff.personal_phone}
                        onChange={(e) => setNewStaff((p) => ({ ...p, personal_phone: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Work Phone</Label>
                      <Input
                        value={newStaff.work_phone}
                        onChange={(e) => setNewStaff((p) => ({ ...p, work_phone: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setStaffDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="create-staff-btn">Create Staff</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Staff Members</CardTitle>
              <CardDescription>{staff.length} staff members registered</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium">Name</th>
                      <th className="text-left py-3 px-4 font-medium">Email</th>
                      <th className="text-left py-3 px-4 font-medium">Positions</th>
                      <th className="text-left py-3 px-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staff.map((member) => (
                      <tr key={member.id} className="border-b table-row-hover">
                        <td className="py-3 px-4 font-medium">
                          <span className="inline-flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: colorForStaff(member) }} />
                            {member.name}
                          </span>
                        </td>
                        <td className="py-3 px-4">{member.email}</td>
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-1">
                            {member.positions?.map((pos) => (
                              <span key={pos} className={`px-2 py-0.5 rounded text-xs ${getPositionBadgeColor(pos)}`}>
                                {pos}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenEditStaff(member)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-100"
                              data-testid={`edit-staff-${member.id}`}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteStaff(member.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-100"
                              data-testid={`delete-staff-${member.id}`}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Edit Staff Dialog */}
          <Dialog open={editStaffDialogOpen} onOpenChange={setEditStaffDialogOpen}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Edit Staff Member</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSaveEditStaff} className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Full Name *</Label>
                    <Input
                      value={editStaffData.name || ""}
                      onChange={(e) => setEditStaffData((p) => ({ ...p, name: e.target.value }))}
                      required
                      data-testid="edit-staff-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={editStaffData.email || ""}
                      onChange={(e) => setEditStaffData((p) => ({ ...p, email: e.target.value }))}
                      required
                      data-testid="edit-staff-email-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date of Birth</Label>
                    <Input
                      type="date"
                      value={editStaffData.dob || ""}
                      onChange={(e) => setEditStaffData((p) => ({ ...p, dob: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Hire Date</Label>
                    <Input
                      type="date"
                      value={editStaffData.hire_date || ""}
                      onChange={(e) => setEditStaffData((p) => ({ ...p, hire_date: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Positions</Label>
                  <div className="flex flex-wrap gap-4">
                    {["Admin", "Teacher", "Clinician", "Special Educator", "HQ Viewer"].map((pos) => (
                      <div key={pos} className="flex items-center space-x-2">
                        <Checkbox
                          id={`edit-pos-${pos}`}
                          checked={editStaffData.positions?.includes(pos)}
                          onCheckedChange={() => toggleEditPosition(pos)}
                        />
                        <Label htmlFor={`edit-pos-${pos}`} className="cursor-pointer">{pos}</Label>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Calendar Color</Label>
                  <ColorSwatchPicker value={editStaffData.color} onChange={(c) => setEditStaffData((p) => ({ ...p, color: c }))} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Emergency Contact Name</Label>
                    <Input
                      value={editStaffData.emergency_contact_name || ""}
                      onChange={(e) => setEditStaffData((p) => ({ ...p, emergency_contact_name: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Emergency Contact Phone</Label>
                    <Input
                      value={editStaffData.emergency_contact_phone || ""}
                      onChange={(e) => setEditStaffData((p) => ({ ...p, emergency_contact_phone: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input
                    value={editStaffData.address || ""}
                    onChange={(e) => setEditStaffData((p) => ({ ...p, address: e.target.value }))}
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      value={editStaffData.city || ""}
                      onChange={(e) => setEditStaffData((p) => ({ ...p, city: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Input
                      value={editStaffData.state || ""}
                      onChange={(e) => setEditStaffData((p) => ({ ...p, state: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>ZIP</Label>
                    <Input
                      value={editStaffData.zip_code || ""}
                      onChange={(e) => setEditStaffData((p) => ({ ...p, zip_code: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Personal Phone</Label>
                    <Input
                      value={editStaffData.personal_phone || ""}
                      onChange={(e) => setEditStaffData((p) => ({ ...p, personal_phone: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Work Phone</Label>
                    <Input
                      value={editStaffData.work_phone || ""}
                      onChange={(e) => setEditStaffData((p) => ({ ...p, work_phone: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setEditStaffDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="save-edit-staff-btn">Save Changes</Button>
                </div>
                <div className="border-t pt-4 mt-2">
                  <Label className="text-sm font-semibold">Reset Password</Label>
                  <div className="flex gap-2 mt-2">
                    <div className="relative flex-1">
                      <Input
                        type={showResetPw ? "text" : "password"}
                        placeholder="New password (min 8 chars, letters + numbers)"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="pr-10"
                        data-testid="reset-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowResetPw((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        aria-label={showResetPw ? "Hide password" : "Show password"}
                        data-testid="toggle-reset-password-visibility"
                      >
                        {showResetPw ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      className="whitespace-nowrap"
                      onClick={async () => {
                        if (newPassword.length < 8 || !/[A-Za-z]/.test(newPassword) || !/\d/.test(newPassword)) {
                          toast.error("Password must be at least 8 characters and include a letter and a number");
                          return;
                        }
                        try {
                          await resetStaffPassword(editStaffData.id, newPassword);
                          toast.success("Password reset successfully");
                          setNewPassword("");
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Failed to reset password");
                        }
                      }}
                      data-testid="reset-password-btn"
                    >
                      Reset Password
                    </Button>
                  </div>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Permissions Info - Now Editable */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Role Permissions</CardTitle>
                <CardDescription>Click &quot;Edit&quot; to customize what each role can access</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={handleResetPermissions}>
                Reset to Defaults
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {["Admin", "Teacher", "Clinician", "Special Educator", "HQ Viewer"].map((role) => {
                  const roleColors = {
                    "Admin": "border-red-200 bg-red-50",
                    "Teacher": "border-blue-200 bg-blue-50",
                    "Clinician": "border-purple-200 bg-purple-50",
                    "Special Educator": "border-green-200 bg-green-50",
                    "HQ Viewer": "border-sky-200 bg-sky-50"
                  };
                  const textColors = {
                    "Admin": "text-red-800",
                    "Teacher": "text-blue-800",
                    "Clinician": "text-purple-800",
                    "Special Educator": "text-green-800",
                    "HQ Viewer": "text-sky-800"
                  };
                  const permissions = rolePermissions[role] || [];
                  
                  return (
                    <div key={role} className={`p-4 rounded-lg border ${roleColors[role]}`}>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className={`font-semibold ${textColors[role]}`}>{role}</h4>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleEditPermissions(role)}
                          data-testid={`edit-${role.toLowerCase().replace(' ', '-')}-permissions`}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                            <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/>
                          </svg>
                          Edit
                        </Button>
                      </div>
                      <ul className="text-sm space-y-1 text-muted-foreground">
                        {permissions.length > 0 ? (
                          permissions.slice(0, 5).map((perm) => (
                            <li key={perm}>• {availablePermissions[perm] || perm}</li>
                          ))
                        ) : (
                          <li className="italic">No permissions set</li>
                        )}
                        {permissions.length > 5 && (
                          <li className="text-xs text-gray-400">+ {permissions.length - 5} more...</li>
                        )}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Edit Permissions Dialog */}
          <Dialog open={permissionDialogOpen} onOpenChange={setPermissionDialogOpen}>
            <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle>Edit {editingRole} Permissions</DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-auto py-4">
                <div className="space-y-3">
                  {Object.entries(availablePermissions).map(([key, label]) => (
                    <div key={key} className="flex items-center space-x-3 p-2 rounded hover:bg-gray-50">
                      <Checkbox
                        id={`perm-${key}`}
                        checked={editPermissions.includes(key)}
                        onCheckedChange={() => handleTogglePermission(key)}
                      />
                      <Label htmlFor={`perm-${key}`} className="cursor-pointer flex-1">
                        {label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setPermissionDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSavePermissions} 
                  disabled={savingPermissions}
                  className="bg-[#00305E] hover:bg-[#00305E]/90"
                >
                  {savingPermissions ? "Saving..." : "Save Permissions"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

      </Tabs>
    </div>
  );
}
